/**
 * ProgressPushRunsPage — /progress-push
 *
 * Lists recent ProgressPushRun records. Every view rendered by this module
 * displays a loud yellow DRY RUN banner so nobody thinks a click here
 * mutates a production database.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const DryRunBanner = ({ liveMode, warning }) => (
  <Alert
    severity={liveMode ? 'error' : 'warning'}
    icon={<WarningIcon />}
    sx={{
      mb: 2,
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: liveMode ? '#d32f2f' : '#ed6c02',
      fontWeight: 600,
    }}
  >
    <AlertTitle sx={{ fontWeight: 'bold' }}>
      {liveMode
        ? 'LIVE MODE — writes CAN hit Nucleos production'
        : 'DRY RUN MODE — No data will be written to production databases'}
    </AlertTitle>
    {warning ||
      (liveMode
        ? 'The PROGRESS_PUSH_LIVE env flag is enabled. Approve actions may result in actual writes.'
        : 'Approve, Reject, and Execute Approved actions only update Automatrix staging records.')}
  </Alert>
);

const StatusChip = ({ status }) => {
  const colorMap = {
    running: 'info',
    completed: 'success',
    dry_run_completed: 'warning',
    failed: 'error',
  };
  return <Chip size="small" label={status} color={colorMap[status] || 'default'} />;
};

export default function ProgressPushRunsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runs, setRuns] = useState([]);
  const [liveMode, setLiveMode] = useState(false);
  const [warning, setWarning] = useState('DRY RUN — no writes to production DBs');
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const [runsResp, healthResp] = await Promise.all([
        fetch('/api/progress-push/runs?limit=50', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/progress-push/health', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!runsResp.ok) throw new Error(`runs HTTP ${runsResp.status}`);
      if (!healthResp.ok) throw new Error(`health HTTP ${healthResp.status}`);
      const runsData = await runsResp.json();
      const healthData = await healthResp.json();
      setRuns(runsData.data || []);
      setLiveMode(!!runsData.liveMode);
      setWarning(runsData.warning || '');
      setHealth(healthData);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <DryRunBanner liveMode={liveMode} warning={warning} />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Progress Push Runs
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Progress Collection runs auto-stage into this queue on success. Each
        run is a DRY-RUN preview of what Automatrix WOULD push back to a
        client's Nucleos database. Nothing here touches production until the
        PROGRESS_PUSH_LIVE env flag is deliberately flipped to "true" in a
        future session.
      </Typography>

      {health && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Health
          </Typography>
          <Typography variant="body2">
            liveMode:{' '}
            <strong style={{ color: health.liveMode ? '#d32f2f' : '#2e7d32' }}>
              {String(health.liveMode)}
            </strong>
            &nbsp;|&nbsp; totalRunsAllTime: <strong>{health.totalRunsAllTime}</strong>
            &nbsp;|&nbsp; totalRecordsWrittenLive:{' '}
            <strong>{health.totalRecordsWrittenLive}</strong>
          </Typography>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Staged</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>App</TableCell>
                <TableCell>Source Script</TableCell>
                <TableCell>Source Execution</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Approved</TableCell>
                <TableCell align="right">Rejected</TableCell>
                <TableCell align="right">Written (mock)</TableCell>
                <TableCell align="right">Written (live)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No push runs yet. Progress Collection scripts auto-stage
                      their results here after a successful headless run.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {runs.map((run) => {
                const s = run.summaryStats || {};
                const proposed = s.recordsProposed ?? 0;
                const approved = s.recordsApproved ?? 0;
                const rejected = s.recordsRejected ?? 0;
                const writtenMock = s.recordsWrittenMock ?? 0;
                const pending = Math.max(
                  0,
                  proposed - approved - rejected - writtenMock,
                );
                return (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      {new Date(run.startedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {run.clientTag ? (
                        <Chip size="small" label={run.clientTag} variant="outlined" />
                      ) : (
                        run.clientId
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={run.appConfigKey} />
                    </TableCell>
                    <TableCell>
                      {run.sourceScriptId ? (
                        <Tooltip title={run.sourceScriptId}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/scripts/${run.sourceScriptId}/edit`);
                            }}
                          >
                            {run.sourceScriptName || run.sourceScriptId.slice(0, 8)}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          (manual)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {run.sourceExecutionLogId && run.sourceScriptId ? (
                        <Tooltip title={run.sourceExecutionLogId}>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<OpenIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/scripts/${run.sourceScriptId}/runs/${run.sourceExecutionLogId}`,
                              );
                            }}
                          >
                            View log
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={run.status} />
                    </TableCell>
                    <TableCell align="right">{proposed}</TableCell>
                    <TableCell align="right">
                      {pending > 0 ? (
                        <Chip size="small" label={pending} color="warning" />
                      ) : (
                        pending
                      )}
                    </TableCell>
                    <TableCell align="right">{approved}</TableCell>
                    <TableCell align="right">{rejected}</TableCell>
                    <TableCell align="right">{writtenMock}</TableCell>
                    <TableCell align="right">
                      <strong
                        style={{
                          color: (s.recordsWritten ?? 0) > 0 ? '#d32f2f' : '#757575',
                        }}
                      >
                        {s.recordsWritten ?? 0}
                      </strong>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Open detail">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/progress-push/runs/${run.id}`)}
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export { DryRunBanner };
